import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { Save, Terminal } from 'lucide-react';
import {
  GlobalRuntimeSettings,
  useGlobalSettings,
  useUpdateGlobalSettings,
} from '../../services/globalSettings.service';
import { EmptyState } from '../../components/admin/EmptyState';
import { useNotification } from '../../hooks/useNotification';
import { GlobalSettingsNumberGrid } from './GlobalSettingsNumberGrid';
import {
  codeAgentFeatureFlagFields,
  codeAgentRuntimeNumberFields,
  codeAgentStreamNumberFields,
  codeAgentSummaryNumberFields,
  type BooleanSettingField,
  type NumberSettingField,
} from './global-settings.config';

const editedFields: (keyof GlobalRuntimeSettings)[] = [
  ...codeAgentRuntimeNumberFields,
  ...codeAgentFeatureFlagFields.map((item) => item.field),
];

const SectionTitle: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Box className="mb-5">
    <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
      {title}
    </Typography>
    <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
      {description}
    </Typography>
  </Box>
);

/** Code Agent runtime settings (rollout flags, stream watchdog, summary thresholds). */
export const CodeAgentRuntimeSettingsPage: React.FC = () => {
  const notification = useNotification();
  const { data, isLoading, error } = useGlobalSettings();
  const updateMutation = useUpdateGlobalSettings();
  const [config, setConfig] = useState<GlobalRuntimeSettings | null>(null);

  useEffect(() => {
    if (data?.config) setConfig(data.config);
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!config || !data?.config) return false;
    return editedFields.some((field) => config[field] !== data.config[field]);
  }, [config, data]);

  const hasInvalidValue =
    config !== null &&
    codeAgentRuntimeNumberFields.some(
      (field) => !Number.isInteger(config[field]) || config[field] <= 0
    );

  const handleNumberChange = (field: NumberSettingField, value: number | undefined) => {
    setConfig((previous) =>
      previous
        ? { ...previous, [field]: value !== undefined && Number.isFinite(value) ? value : 0 }
        : previous
    );
  };

  const handleBooleanChange = (field: BooleanSettingField, value: boolean) => {
    setConfig((previous) => (previous ? { ...previous, [field]: value } : previous));
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      await updateMutation.mutateAsync(config);
      notification.success('Code Agent runtime settings updated');
    } catch (saveError) {
      notification.error(
        saveError instanceof Error ? saveError.message : 'Failed to update Code Agent settings'
      );
    }
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      <Box className="mb-6 flex items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Code Agent Runtime Settings
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Rollout flags, model-stream watchdog and rolling-summary thresholds for the coding
            agent. Per-turn budgets, prompt budget and kill switches are under Policy &amp; Kill
            Switches.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            updateMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Save size={20} />
            )
          }
          onClick={handleSave}
          disabled={!hasChanges || hasInvalidValue || updateMutation.isPending}
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': { backgroundColor: '#2563EB' },
            '&:disabled': { backgroundColor: '#94a3b8' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {isLoading ? (
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-blue-500" />
        </Box>
      ) : error || !config ? (
        <EmptyState
          Icon={Terminal}
          title="Failed to load Code Agent settings"
          description="Please try again later"
        />
      ) : (
        <Paper
          elevation={0}
          className="rounded-xl bg-white p-6 dark:bg-slate-800"
          sx={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}
        >
          {hasInvalidValue && (
            <Alert severity="error" className="mb-5">
              Numeric settings must be positive integers.
            </Alert>
          )}

          <SectionTitle
            title="Rollout flags"
            description="Phase switches from docs/reviews/code-agent-specs. Enable one phase at a time and compare code_agent.turn.completed_metrics before and after."
          />
          <Box className="mb-5 grid gap-2">
            {codeAgentFeatureFlagFields.map((item) => (
              <Box key={item.field}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(config[item.field])}
                      onChange={(event) => handleBooleanChange(item.field, event.target.checked)}
                    />
                  }
                  label={item.label}
                />
                <Typography
                  variant="caption"
                  className="block pl-12 text-gray-600 dark:text-slate-400"
                >
                  {item.helperText}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider className="my-6" />

          <SectionTitle
            title="Stream watchdog"
            description="Global defaults for the model-stream watchdog. A model can override both values via its LLM catalog config JSON (firstChunkTimeoutMs, streamStallTimeoutMs)."
          />
          <GlobalSettingsNumberGrid
            config={config}
            fields={codeAgentStreamNumberFields}
            onNumberChange={handleNumberChange}
          />

          <Divider className="my-6" />

          <SectionTitle
            title="Rolling summary"
            description={`Thresholds for creating the Code Agent rolling summary. The master switch "Enable conversation summaries" stays in Global Settings (${
              config.conversationSummaryEnabled ? 'currently on' : 'currently off'
            }).`}
          />
          <GlobalSettingsNumberGrid
            config={config}
            fields={codeAgentSummaryNumberFields}
            onNumberChange={handleNumberChange}
          />
        </Paper>
      )}
    </Box>
  );
};

export default CodeAgentRuntimeSettingsPage;
